import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Send, Copy } from "lucide-react";
import { IconX } from "./icons";

const BURN_TOOLTIP =
  "Creator pledged buy-back-and-burn — every fee claim swaps SOL into this token and burns the supply forever.";
const LP_TOOLTIP =
  "Creator's fees auto-compound back into the LP — pool reserves grow forever, creator earns nothing in SOL.";

/**
 * Compact USD formatter.
 *   $842.3K  •  $1.32M  •  $0.0048
 */
const fmtUsd = (n) => {
  if (n === null || n === undefined || Number.isNaN(+n)) return "—";
  const v = +n;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

// Round to whole number — fractional % takes too much room on the polaroid
// card and overflows when the value gets big (e.g. +1234.56% wraps).
const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(+n)) return null;
  const v = +n;
  const sign = v > 0 ? "+" : v < 0 ? "" : "";
  return `${sign}${Math.round(v)}%`;
};

const fmtCount = (n) => {
  if (n === null || n === undefined) return "—";
  const v = +n;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};

const fmtAge = (seconds) => {
  if (!seconds && seconds !== 0) return "—";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
};

const shortAddr = (addr) => {
  if (!addr) return "—";
  if (addr.length <= 10) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

/**
 * PremiumCard — redesigned token card. Accepts the same token object shape
 * used throughout the dashboard (name, symbol, contractAddress, image,
 * market_cap_usd, volume_usd_24h, etc.) plus optional extras (sparkline,
 * holders, age_seconds, price_change_24h).
 */
export default function PremiumCard({
  token,
  onCopy,
  clickable = true,
  bumpKind = null,   // "trade" | "launch" | null
  bumpTs = 0,        // timestamp — change forces animation restart
}) {
  const navigate = useNavigate();

  const changeDir = useMemo(() => {
    const c = token?.price_change_24h;
    if (c == null) return null;
    if (c > 0) return "up";
    if (c < 0) return "down";
    return null;
  }, [token?.price_change_24h]);

  const handleCardClick = useCallback(
    (e) => {
      if (!clickable) return;
      if (e.target.closest("[data-stop-click]")) return;
      if (!token?.contractAddress) return;
      navigate(`/token/${token.contractAddress}`);
    },
    [clickable, navigate, token?.contractAddress]
  );

  const stopAndRun = (fn) => (e) => {
    e.stopPropagation();
    fn?.(e);
  };

  const copy = stopAndRun(() => {
    if (onCopy) return onCopy(token.contractAddress);
    if (navigator?.clipboard) navigator.clipboard.writeText(token.contractAddress || "");
  });

  const hasSocials = token?.website || token?.telegram || token?.twitter;
  const pct = fmtPct(token?.price_change_24h);

  const bumpClass = bumpKind === "trade"
    ? "pm-card--bump"
    : bumpKind === "launch"
    ? "pm-card--launch"
    : "";

  return (
    <div
      className={`pm-card ${bumpClass}`.trim()}
      data-demo={token?._demo ? "true" : undefined}
      data-preview={!clickable ? "true" : undefined}
      data-bump={bumpKind || undefined}
      data-bumpts={bumpTs || undefined}
      onClick={handleCardClick}
    >
      <div className="pm-card__top">
        <div className="pm-card__image" data-ticker={token?.symbol ? `$${token.symbol}` : ""}>
          {token?.image?.url ? (
            <img src={token.image.url} alt={token.name} />
          ) : (
            <div className="pm-card__image-ph" />
          )}
        </div>

        <div className="pm-card__info">
          <div className="pm-card__name-row">
            <h3 className="pm-card__name" title={token?.name}>
              {token?.name || "Untitled"}
            </h3>
            <button
              className="pm-card__copy"
              onClick={copy}
              title={`Copy ${shortAddr(token?.contractAddress)}`}
              aria-label="Copy contract address"
              data-stop-click
            >
              <Copy />
            </button>
          </div>

          <div className="pm-card__ticker-row">
            <span className="pm-card__ticker">${token?.symbol || "—"}</span>
          </div>

          <div className="pm-card__mc-row">
            <div className="pm-card__mc">{fmtUsd(token?.market_cap_usd)}</div>
            {token?.fee_mode === "burn" && (
              <span
                className="pm-card__burn-sticky"
                title={BURN_TOOLTIP}
                aria-label="Fees are burnt"
              >
                Fees<br />burnt!
              </span>
            )}
            {token?.fee_mode === "lp_compound" && (
              <span
                className="pm-card__lp-sticky"
                title={LP_TOOLTIP}
                aria-label="Fees go to LP"
              >
                Fees<br />grow LP!
              </span>
            )}
            {token?.dex_paid_at && (
              <span
                className="pm-card__dex-sticky"
                title="Creator paid for the DexScreener profile (uploaded header image + extra info)"
                aria-label="DEX profile paid"
              >
                DEX<br />paid!
              </span>
            )}
          </div>
        </div>
      </div>

      {hasSocials && (
        <div className="pm-card__socials" data-stop-click>
          {token?.website && (
            <a
              className="pm-card__social"
              href={token.website.startsWith("http") ? token.website : `https://${token.website}`}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="Website"
            ><Globe /></a>
          )}
          {token?.telegram && (
            <a
              className="pm-card__social"
              href={token.telegram}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="Telegram"
            ><Send /></a>
          )}
          {token?.twitter && (
            <a
              className="pm-card__social"
              href={token.twitter}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label="X"
            ><IconX /></a>
          )}
        </div>
      )}

      <div className="pm-card__stats">
        <div className="pm-card__stat">
          <span className="pm-card__stat-label">VOL 24H</span>
          <span className="pm-card__stat-value">{fmtUsd(token?.volume_usd_24h)}</span>
        </div>
        <div className="pm-card__stat">
          <span className="pm-card__stat-label">HOLDERS</span>
          <span className="pm-card__stat-value">{fmtCount(token?.holders)}</span>
        </div>
        <div className="pm-card__stat">
          <span className="pm-card__stat-label">24H</span>
          <span
            className={`pm-card__stat-value ${
              changeDir === "up" ? "pm-card__stat-value--up" :
              changeDir === "down" ? "pm-card__stat-value--down" : ""
            }`}
          >
            {pct || "—"}
          </span>
        </div>
        <div className="pm-card__stat">
          <span className="pm-card__stat-label">AGE</span>
          <span className="pm-card__stat-value">{fmtAge(token?.age_seconds)}</span>
        </div>
      </div>
    </div>
  );
}
